import {
  CheckOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Button,
  Divider,
  Flex,
  Modal,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import moment from 'moment';

import type { PayType } from '@app/api/PaymentAccounts';
import type { ModelRecordPayment } from '@app/api/Records/types';
import { CloseIconsForModalWithDataTestId } from '@app/components/CloseIconsForModalWithDataTestId';
import type { HandlePayByYouKassaProps } from '@app/components/RecordModal/types';
import { ACQUIRING, DATE_PICKER_FORMAT, LOYALTY, PREFIX } from '@app/constants';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { divideAmount } from '@app/utils';

import type { FC } from 'react';

import '@app/components/RecordModal/components/RecordPayment/styles/FinancialOperations.desktop.scss';

const { Text } = Typography;

const definePaymentLabel = (
  paymentType: PayType,
  IsPrepay: boolean,
  IsFromBalance: boolean
) => {
  if (IsFromBalance) {
    return 'Оплата с баланса, ';
  }

  switch (paymentType) {
    case 'bank':
      if (IsPrepay) {
        return 'Предоплата картой, ';
      }

      return 'Оплата картой, ';
    case 'cash':
      if (IsPrepay) {
        return 'Предоплата наличными, ';
      }

      return 'Оплата наличными, ';
    case ACQUIRING:
      if (IsPrepay) {
        return 'Предоплата через эквайринг, ';
      }

      return 'Оплата через эквайринг, ';
    case LOYALTY:
      return 'Использовано бонусов по карте лояльности';
    default:
      if (IsPrepay) {
        return 'Неизвестный тип предоплаты';
      }

      return 'Неизвестный тип оплаты';
  }
};

const handleAccountName = (name: string) => {
  const firstSymbolInLowerCase = name.charAt(0).toLowerCase();
  const nameRest = name.substring(1, name.length);

  return firstSymbolInLowerCase + nameRest;
};

interface Props {
  totalAmount: string;
  totalPaid: number;
  isDeletePaymentLoading: boolean;
  hasPayments: boolean;
  payments: ModelRecordPayment[] | null;
  acquiringPaymentMethodNames: Record<number, Record<string, string>>;
  hasUserRightToDeleteSale: boolean;
  isOpenConfirmationDialog: boolean;
  startPaymentDeletion(): Promise<void>;
  closeDeletePaymentModal(): void;
  openDeletePeymentModal(paymentId: number): void;
  handlePaymentInfoModalOpen: (paymentId: number | null) => void;
  handlePayByYouKassa: ({
    Amount,
    PaymentMethod,
    IsNeedToCreatePayment,
  }: HandlePayByYouKassaProps) => void;
  setUseEmailForPayment: React.Dispatch<React.SetStateAction<boolean>>;
}

export const FinancialOperations: FC<Props> = ({
  totalAmount,
  totalPaid,
  isDeletePaymentLoading,
  hasPayments,
  payments,
  acquiringPaymentMethodNames,
  hasUserRightToDeleteSale,
  isOpenConfirmationDialog,
  startPaymentDeletion,
  closeDeletePaymentModal,
  openDeletePeymentModal,
  handlePaymentInfoModalOpen,
  handlePayByYouKassa,
  setUseEmailForPayment,
}) => {
  if (isDeletePaymentLoading) {
    return (
      <div className={`${PREFIX}calendar-payments empty`}>
        <Skeleton active />
      </div>
    );
  }

  if (!hasPayments) {
    return null;
  }

  return (
    <Space
      direction="vertical"
      className={`${PREFIX}financial-operations full-width`}
      size={0}
    >
      <Flex justify="space-between">
        <Text strong data-testid="paid">
          Оплачено
        </Text>
        <Text strong data-testid="total-paid-amount">
          {divideAmount(totalPaid)}
        </Text>
      </Flex>
      <Divider
        className="payments-table-header-bottom full-width"
        style={{ margin: '8px 0px' }}
      />

      {hasPayments && (
        <Space direction="vertical" className="full-width" size={0}>
          {payments!.map(
            (
              {
                PaymentId,
                PaymentType,
                PaymentAccount,
                TimeCreated,
                Amount,
                IsPrepay,
                Comment,
                IsFromBalance,
                IsSuccess,
                Status,
                PaymentMethod,
                PaymentAccountId,
              },
              index
            ) => (
              <Flex
                key={`${PaymentId}-${TimeCreated}-${Amount}-${index}`}
                className="payment-row"
                justify="space-between"
                align="center"
                data-testid="payments-item"
              >
                <div
                  className={`payment-type ${
                    PaymentType === ACQUIRING && 'acquiring-type'
                  }`}
                  onClick={
                    PaymentType === ACQUIRING && !IsSuccess
                      ? () => {
                          setUseEmailForPayment(Status === 'awaiting_email');
                          handlePayByYouKassa({
                            Amount,
                            PaymentMethod,
                            IsNeedToCreatePayment: false,
                            ClientPaymentId: PaymentId,
                            IsNeedToCreateLink: Status === 'awaiting_email',
                            PaymentAccountId,
                          });
                        }
                      : () => handlePaymentInfoModalOpen(PaymentId)
                  }
                >
                  <Text data-testid="payment-method">
                    {`${definePaymentLabel(
                      PaymentType,
                      IsPrepay,
                      IsFromBalance
                    )}`}
                    <Text type="secondary" data-testid="cash-register-name">
                      {PaymentAccount ? handleAccountName(PaymentAccount) : ''}
                    </Text>
                    {PaymentType === ACQUIRING &&
                      PaymentMethod &&
                      acquiringPaymentMethodNames[PaymentAccountId]?.[
                        PaymentMethod
                      ] && (
                        <Text
                          type="secondary"
                          data-testid="acquiring-payment-method-name"
                        >
                          {` (${acquiringPaymentMethodNames[PaymentAccountId][PaymentMethod]})`}
                        </Text>
                      )}
                    {Comment && (
                      <Tooltip title={Comment}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    )}
                  </Text>{' '}
                  {PaymentType === ACQUIRING && Status === 'pending' && (
                    <Tag icon={<WarningOutlined />} color="#cd201f">
                      Ожидание оплаты
                    </Tag>
                  )}
                  {PaymentType === ACQUIRING && Status === 'awaiting_email' && (
                    <Tag icon={<WarningOutlined />} color="#cd201f">
                      Ожидание ввода email
                    </Tag>
                  )}
                </div>

                <Space>
                  {PaymentType === ACQUIRING && IsSuccess && (
                    <CheckOutlined style={{ color: '#52c41a' }} />
                  )}
                  <div className="payment-time" data-testid="payment-date">
                    {moment(TimeCreated).format(DATE_PICKER_FORMAT[0])}
                  </div>
                  <div
                    className="payment-amount"
                    data-testid="payment-amount"
                  >{`${divideAmount(Amount)}`}</div>
                  {hasUserRightToDeleteSale && (
                    <div className="payment-action">
                      {PaymentType !== LOYALTY && (
                        <Button
                          danger
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => openDeletePeymentModal(PaymentId)}
                          data-testid="delete-payment-icon"
                        />
                      )}
                    </div>
                  )}
                </Space>
              </Flex>
            )
          )}
        </Space>
      )}
      <Divider
        className="payments-table-footer-top full-width"
        style={{ margin: '8px 0px' }}
      />

      <Flex justify="space-between">
        <Text strong data-testid="total-to-pay">
          Итого к оплате
        </Text>
        <Text strong data-testid="total-to-pay-amount">
          {divideAmount(totalAmount)}
        </Text>
      </Flex>

      <Modal
        title={generateSpanWithDataTestId(
          'Подтверждение операции',
          'confirmation-operation'
        )}
        okText="Удалить"
        okButtonProps={{
          danger: true,
          'data-testid': 'delete',
          disabled: isDeletePaymentLoading,
        }}
        cancelButtonProps={{
          'data-testid': 'cancel',
          loading: isDeletePaymentLoading,
        }}
        open={isOpenConfirmationDialog}
        onOk={startPaymentDeletion}
        onCancel={closeDeletePaymentModal}
        closeIcon={<CloseIconsForModalWithDataTestId />}
      >
        <Text data-testid="delete-payment-confirmation">
          Вы уверены, что хотите удалить платеж?
        </Text>
      </Modal>
    </Space>
  );
};
